import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Search, MoreVertical, Eye, Pencil, Trash2, Plus } from "lucide-react";
import BottomNav from "../../components/BottomNav.tsx";
import { baseurly, X_API_KEY } from '../../utils/apis.ts';
import { baseurl } from "../../utils/apis.ts";
import { digitalOceanImageUrl } from "../../utils/digitalOcean.ts";


interface VehicleModel {
  id: number;
  model_name: string;
  image?: string;
}

const AddModelPage: React.FC = () => {
  const [vehicles, setVehicles] = useState<VehicleModel[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showDropdown, setShowDropdown] = useState<number | null>(null);
  const navigate = useNavigate();

  // Fetch vehicle models from the API
  useEffect(() => {
    fetch(`${baseurly}/vehicle_models`, {
      method: "GET",
      headers: {
        'X-API-KEY': X_API_KEY,
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error("Failed to fetch vehicle models");
        }
        return res.json();
      })
      .then((data) => {
        setVehicles(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching vehicle models:", err);
        setError("Unable to load vehicle models");
        setLoading(false);
      });
  }, []);

  // Handle delete action
  const handleDelete = async (id: number) => {
    try {
      const response = await fetch(`${baseurly}/vehicle_model/${id}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error("Failed to delete vehicle model");
      }

      // Remove deleted model from UI
      setVehicles((prev) => prev.filter((vehicle) => vehicle.id !== id));
      setShowDropdown(null);
    } catch (error) {
      console.error("Error deleting vehicle model:", error);
    }
  };

  return (
    <div className="min-h-screen bg-blue-900 pb-20">
      {/* Header */}
      <div className="p-4 flex justify-between items-center">
        <h1 className="text-white text-xl font-bold">All Vehicle Models</h1>
        <img
          src={digitalOceanImageUrl("new%20logos/Asset%204.png")}
          alt="KMC Logo"
          className="w-16 h-auto"
        />
      </div>

      {/* Search Bar */}
      <div className="px-6 mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search for an instruction document..."
            className="w-full py-3 px-4 pr-10 rounded-full"
          />
          <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
        </div>
      </div>

      {/* Vehicle Models Section */}
      <div className="bg-white rounded-3xl px-6 py-6 ">
        <h2 className="text-blue-900 text-xl font-bold mb-4">Vehicle Models</h2>

        {/* Show loading or error messages */}
        {loading ? (
          <p className="text-gray-600">Loading vehicle models...</p>
        ) : error ? (
          <p className="text-red-500">{error}</p>
        ) : vehicles.length > 0 ? (
          <div className="grid grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-4">
            {vehicles.map((vehicle) => (
              <div key={vehicle.id} className="bg-white border rounded-xl p-4 relative shadow-md">
                {/* Model Image */}
                <img
                  src={
                    vehicle.image
                    ? 
                      `${baseurl}files/${vehicle.image}`
                    : 
                      digitalOceanImageUrl("assets/images/b.png")
                  }
                  alt={vehicle.model_name}
                  className="h-20 object-cover rounded-lg mb-2"
                />

                {/* Model Info */}
                <h3 className="font-bold text-sm">{vehicle.model_name}</h3>
                <p className="text-sm text-gray-600">Work Instructions</p>

                {/* Action Dropdown */}
                <button
                  className="absolute top-2 right-2 p-2"
                  onClick={() => setShowDropdown(showDropdown === vehicle.id ? null : vehicle.id)}
                >
                  <MoreVertical className="w-5 h-5 text-gray-700" />
                </button>

                {showDropdown === vehicle.id && (
                  <div className="absolute right-2 top-8 bg-white shadow-lg rounded-lg p-2 w-24">
                    <button
                      className="flex items-center gap-2 p-2 w-full text-sm hover:bg-gray-100"
                      onClick={() => navigate(`/vehicle/${vehicle.id}`)}
                    >
                      <Eye className="w-4 h-4" /> View
                    </button>
                    <button className="flex items-center gap-2 p-2 w-full text-sm hover:bg-gray-100">
                      <Pencil className="w-4 h-4" /> Edit
                    </button>
                    <button
                      className="flex items-center gap-2 p-2 w-full text-sm text-red-500 hover:bg-gray-100"
                      onClick={() => handleDelete(vehicle.id)}
                    >
                      <Trash2 className="w-4 h-4" /> Delete
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-600">No vehicle models found.</p>
        )}

        {/* Add New Model Button */}
        <div className="text-center font-bold mt-6 hover:font-bold text-white transition-transform transform hover:scale-105">
          <button
            className="bg-blue-900 text-white px-6 py-3 rounded-full flex items-center gap-2 mx-auto"
            onClick={() => navigate("/create-model")}
          >
            <Plus className="w-5 h-5 font-bold hover:font-bold text-white transition-transform transform hover:scale-110" />
            Add New Model
          </button>
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNav showBack={true} />
    </div>
  );
};

export default AddModelPage;


